import type { AdminPermission, AdminUser } from "./types";

/**
 * The group and form field for each permission a role can grant.
 *
 * `is_readonly`, `hide_wage` and `disable_edit_master_schedule` are stored as restrictions but shown
 * as positive switches ("Can edit", "Can see wages", "Can edit Schedule Template"), so every switch
 * on means more access.
 *
 * The grouping is the one presentational choice: seven of these are email digests, not access.
 */

/** Which block a permission belongs to. */
export type AdminPermissionGroup = "permissions" | "notifications";

export type AdminPermissionMeta = {
	permission: AdminPermission;
	/** The form field holding it. */
	field: keyof AdminUser;
	group: AdminPermissionGroup;
	/** The switch shows the opposite of the stored value. Set for the three restrictions. */
	inverted: boolean;
	/** i18n key suffix, for the label and its tooltip. */
	i18nKey: string;
};

/**
 * Keyed by permission name so the catalog is exhaustive by construction: a new `AdminPermission`
 * without an entry here is a compile error, rather than one `applyRoleChange` writes but no screen
 * shows.
 *
 * `sendDailyActualShiftsCheck` is absent on purpose — no role grants it.
 */
const CATALOG_BY_PERMISSION = {
	is_readonly: { permission: "is_readonly", field: "isReadonly", group: "permissions", inverted: true, i18nKey: "canEdit" },
	disable_edit_master_schedule: {
		permission: "disable_edit_master_schedule",
		field: "disableEditMasterSchedule",
		group: "permissions",
		inverted: true,
		i18nKey: "canEditMasterSchedule",
	},
	call_off_admin: { permission: "call_off_admin", field: "callOffAdmin", group: "permissions", inverted: false, i18nKey: "callOffAdmin" },
	hide_wage: { permission: "hide_wage", field: "hideWage", group: "permissions", inverted: true, i18nKey: "canSeeWage" },
	modify_user: { permission: "modify_user", field: "modifyUser", group: "permissions", inverted: false, i18nKey: "modifyUser" },
	send_facility_messages: {
		permission: "send_facility_messages",
		field: "sendFacilityMessages",
		group: "permissions",
		inverted: false,
		i18nKey: "sendFacilityMessages",
	},
	receive_daily_admin_notifications: {
		permission: "receive_daily_admin_notifications",
		field: "receiveDailyAdminNotifications",
		group: "notifications",
		inverted: false,
		i18nKey: "receiveDailyAdminNotifications",
	},
	receive_action_required_admin_notifications: {
		permission: "receive_action_required_admin_notifications",
		field: "receiveActionRequiredAdminNotifications",
		group: "notifications",
		inverted: false,
		i18nKey: "receiveActionRequiredAdminNotifications",
	},
	send_daily_pickup_summary: {
		permission: "send_daily_pickup_summary",
		field: "sendDailyPickupSummary",
		group: "notifications",
		inverted: false,
		i18nKey: "sendDailyPickupSummary",
	},
	receive_performance_report: {
		permission: "receive_performance_report",
		field: "receivePerformanceReport",
		group: "notifications",
		inverted: false,
		i18nKey: "receivePerformanceReport",
	},
	receive_daily_excel_report: {
		permission: "receive_daily_excel_report",
		field: "receiveDailyExcelReport",
		group: "notifications",
		inverted: false,
		i18nKey: "receiveDailyExcelReport",
	},
	receive_sms_response_notifications: {
		permission: "receive_sms_response_notifications",
		field: "receiveSmsResponseNotifications",
		group: "notifications",
		inverted: false,
		i18nKey: "receiveSmsResponseNotifications",
	},
	receive_nurse_inactivity_notifications: {
		permission: "receive_nurse_inactivity_notifications",
		field: "receiveNurseInactivityNotifications",
		group: "notifications",
		inverted: false,
		i18nKey: "receiveNurseInactivityNotifications",
	},
} as const satisfies Record<AdminPermission, AdminPermissionMeta>;

/** Display order, also the order of the summary-bar segments. */
export const ADMIN_PERMISSION_CATALOG: readonly AdminPermissionMeta[] = Object.values(CATALOG_BY_PERMISSION);

export const ADMIN_PERMISSION_ITEMS = ADMIN_PERMISSION_CATALOG.filter((p) => p.group === "permissions");
export const ADMIN_NOTIFICATION_ITEMS = ADMIN_PERMISSION_CATALOG.filter((p) => p.group === "notifications");

/** Delivered by text, so useless without a phone number. */
const PHONE_DEPENDENT_PERMISSIONS: readonly AdminPermission[] = ["call_off_admin", "receive_sms_response_notifications"] as const;

export const needsPhoneNumber = (permission: AdminPermission): boolean => PHONE_DEPENDENT_PERMISSIONS.includes(permission);

/** Is this permission ticked for this row? */
export const isGranted = (row: AdminUser, meta: AdminPermissionMeta): boolean => {
	const stored = row[meta.field] === true;
	return meta.inverted ? !stored : stored;
};

/** The value to store for a wanted checkbox state. */
export const storedValueForGrant = (meta: AdminPermissionMeta, granted: boolean): boolean => (meta.inverted ? !granted : granted);

/** Access permissions are grouped in the panel. */
export type AdminPermissionSubgroup = "scheduling" | "peopleAndPay" | "messaging";

/** Panel subgroup. Notifications are one flat list. */
export const PERMISSION_SUBGROUP: Partial<Record<AdminPermission, AdminPermissionSubgroup>> = {
	is_readonly: "scheduling",
	disable_edit_master_schedule: "scheduling",
	call_off_admin: "scheduling",
	hide_wage: "peopleAndPay",
	modify_user: "peopleAndPay",
	send_facility_messages: "messaging",
};

export const permissionsInSubgroup = (subgroup: AdminPermissionSubgroup): AdminPermissionMeta[] =>
	ADMIN_PERMISSION_ITEMS.filter((meta) => PERMISSION_SUBGROUP[meta.permission] === subgroup);
